function truncate(value, max = 1200) {
  const text = String(value ?? "");
  return text.length > max ? `${text.slice(0, max)}...[truncated:${text.length - max}]` : text;
}

function serialize(value, depth = 0) {
  if (depth > 4) return "[depth-truncated]";
  if (value instanceof Error) {
    return {
      name: value.name,
      message: truncate(value.message, 600),
      stack: truncate(value.stack || "", 1200),
    };
  }
  if (value === null || value === undefined) return value;
  if (typeof value === "string") return truncate(value, 1200);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "function") return `[function:${value.name || "anonymous"}]`;
  if (Array.isArray(value)) return value.slice(0, 20).map((item) => serialize(item, depth + 1));
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, 25)
        .map(([key, innerValue]) => [key, serialize(innerValue, depth + 1)])
    );
  }
  return truncate(value, 500);
}

function stringifyArg(value) {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message || String(value);
  try {
    return JSON.stringify(serialize(value));
  } catch (error) {
    return "[unserializable]";
  }
}

function normalizeLevel(level, message) {
  const text = String(message || "").toLowerCase();
  if (level === "debug" || level === "info") {
    if (/(error|failed|exception|invalid|unable|unauthorized|unexpected|fatal)/.test(text)) {
      return "error";
    }
    if (/(warn|warning|offline|not found|mismatch|skipping|fallback|expired|locked)/.test(text)) {
      return "warn";
    }
  }
  return level;
}

function getOriginalConsole(level) {
  if (typeof window === "undefined") return null;
  const original = window.__MEA_USER_TRACER_ORIGINAL_CONSOLE__;
  return original?.[level] || original?.log || null;
}

function emit(level, namespace, args, defaults = {}) {
  if (typeof window === "undefined") return;
  const tracer = window.__MEA_USER_TRACER__;
  const original = getOriginalConsole(level);
  if (tracer?.isLocalhost?.()) {
    original?.(...args);
    return;
  }

  const message = args.map(stringifyArg).join(" ");
  const resolvedLevel = normalizeLevel(level, message);
  const eventType = defaults.eventType || (resolvedLevel === "warn" || resolvedLevel === "error" ? "ui-log-error" : "ui-log");
  const category = defaults.category || (resolvedLevel === "warn" || resolvedLevel === "error" ? "USER_ERROR" : "UI_ACTIVITY");
  tracer?.trace?.(resolvedLevel, eventType, message, {
    ...defaults,
    category,
    data: {
      namespace,
      args: serialize(args),
      ...(defaults.data || {}),
    },
  });
}

export function createUiLogger(namespace, defaults = {}) {
  return {
    log: (...args) => emit("info", namespace, args, defaults),
    info: (...args) => emit("info", namespace, args, defaults),
    debug: (...args) => emit("debug", namespace, args, defaults),
    warn: (...args) => emit("warn", namespace, args, defaults),
    error: (...args) => emit("error", namespace, args, defaults),
    trace: (...args) => emit("debug", namespace, args, defaults),
  };
}
